// Pure email personalization logic, extracted from the sendCampaignNow pipeline in
// src/App.jsx so it can be unit-tested. Behavior is identical to the original
// inline implementation (including the double-brace-first replacement fix).

export const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const replaceToken = (content, token, value) => {
  // Match double braces FIRST so {{token}} isn't partially consumed by the single-brace pass
  const doubleBraceRegex = new RegExp(`\\{\\{\\s*${escapeRegExp(token)}\\s*\\}\\}`, 'gi')
  // Match single braces: {token} or { token }
  const singleBraceRegex = new RegExp(`\\{\\s*${escapeRegExp(token)}\\s*\\}`, 'gi')
  return content.replace(doubleBraceRegex, value).replace(singleBraceRegex, value)
}

export const resolveContactToken = (match, contact) => {
  const field = match.replace(/[\{\}\s]/g, '').toLowerCase().replace('contact.', '')
  if (field.includes('first')) return contact.name?.split(' ')[0] || 'there'
  if (field.includes('last')) return contact.name?.split(' ').slice(1).join(' ') || ''
  if (field.includes('company')) return contact.company || 'your company'
  if (field.includes('email')) return contact.email || ''
  if (field.includes('name')) return contact.name || 'there'
  return ''
}

export const cleanupContactTokens = (text, contact) =>
  text.replace(/\{\{\s*contact\.[^}]*?\s*\}\}|\{\s*contact\.[^}]*?\s*\}/gi, (match) => resolveContactToken(match, contact))

// Replaces every supported personalization token ({token}, {{token}}, {contact.X}, {{contact.X}})
// with a clean value. Guarantees no curly brackets remain in the output.
export const personalizeEmailContent = (content, contact) => {
  if (typeof content !== 'string') return content
  let personalized = content

  const firstName = contact.name?.split(' ')[0] || 'there'
  const lastName = contact.name?.split(' ').slice(1).join(' ') || ''
  const fullName = contact.name || 'there'
  const company = contact.company || 'your company'
  const email = contact.email || ''

  // Handle all common variations of first name (including CRM export style: contact.firstName, contact.FIRSTNAME)
  personalized = replaceToken(personalized, 'firstName', firstName)
  personalized = replaceToken(personalized, 'first_name', firstName)
  personalized = replaceToken(personalized, 'First Name', firstName)
  personalized = replaceToken(personalized, 'first name', firstName)
  personalized = replaceToken(personalized, 'FIRSTNAME', firstName)
  personalized = replaceToken(personalized, 'contact.firstName', firstName)
  personalized = replaceToken(personalized, 'contact.FIRSTNAME', firstName)
  personalized = replaceToken(personalized, 'contact.FirstName', firstName)
  personalized = replaceToken(personalized, 'contact.first_name', firstName)

  // Handle all common variations of last name
  personalized = replaceToken(personalized, 'lastName', lastName)
  personalized = replaceToken(personalized, 'last_name', lastName)
  personalized = replaceToken(personalized, 'Last Name', lastName)
  personalized = replaceToken(personalized, 'last name', lastName)
  personalized = replaceToken(personalized, 'LASTNAME', lastName)
  personalized = replaceToken(personalized, 'contact.lastName', lastName)
  personalized = replaceToken(personalized, 'contact.LASTNAME', lastName)
  personalized = replaceToken(personalized, 'contact.LastName', lastName)
  personalized = replaceToken(personalized, 'contact.last_name', lastName)

  // Handle all common variations of full name
  personalized = replaceToken(personalized, 'name', fullName)
  personalized = replaceToken(personalized, 'Name', fullName)
  personalized = replaceToken(personalized, 'full_name', fullName)
  personalized = replaceToken(personalized, 'Full Name', fullName)
  personalized = replaceToken(personalized, 'contact.name', fullName)
  personalized = replaceToken(personalized, 'contact.Name', fullName)

  // Handle all common variations of company
  personalized = replaceToken(personalized, 'company', company)
  personalized = replaceToken(personalized, 'Company', company)
  personalized = replaceToken(personalized, 'company_name', company)
  personalized = replaceToken(personalized, 'Company Name', company)
  personalized = replaceToken(personalized, 'COMPANY', company)
  personalized = replaceToken(personalized, 'contact.company', company)
  personalized = replaceToken(personalized, 'contact.Company', company)
  personalized = replaceToken(personalized, 'contact.COMPANY', company)

  // Email
  personalized = replaceToken(personalized, 'contact.email', email)

  // Final safety net: remove any remaining contact.* tokens so no curly brackets ever leak into sent emails
  personalized = cleanupContactTokens(personalized, contact)

  return personalized
}
